import { useQuery } from '@tanstack/react-query';
import { cpFetch } from '@/lib/cpClient';
import { useAuthStore } from '@/store/authStore';
import { useSelectedAppId } from '@/hooks/useSelectedAppId';

export interface MapperLayerConfig {
  enabled: boolean;
  label_singular?: string;
  label_plural?: string;
  group_label?: string;
  poll_interval_ms?: number;
  trail_window?: string;
  palette?: string[];
  detect_enabled?: boolean;
  datasets?: string[];
}

export interface MapperPluginConfig {
  enabled: boolean;
  layers: {
    basemap: MapperLayerConfig;
    reference: MapperLayerConfig;
    land_units: MapperLayerConfig;
    objects: MapperLayerConfig;
    live: MapperLayerConfig;
    analysis: MapperLayerConfig;
  };
  embed?: {
    allowed_origins?: string[];
    token_ttl_seconds?: number;
  };
}

const DEFAULT_CONFIG: MapperPluginConfig = {
  enabled: true,
  layers: {
    basemap: { enabled: true },
    reference: { enabled: true },
    land_units: {
      enabled: true,
      label_singular: 'Land unit',
      label_plural: 'Land units',
      group_label: 'Group',
    },
    objects: { enabled: true, palette: [], detect_enabled: false },
    live: { enabled: false, poll_interval_ms: 15000, trail_window: '24h' },
    analysis: { enabled: false, datasets: [] },
  },
};

export function useMapperPluginConfig(options: { enabled?: boolean } = {}) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  const explicitEnabled = options.enabled !== undefined ? options.enabled : true;

  return useQuery({
    queryKey: ['mapper', 'plugin-config', appId],
    queryFn: async () => {
      const res = await cpFetch('/mapper/plugin/config');
      if (!res.ok) throw new Error('Failed to load mapper config');
      const json = await res.json();
      return {
        manifest: json.manifest,
        config: { ...DEFAULT_CONFIG, ...(json.config || {}) } as MapperPluginConfig,
      };
    },
    enabled: explicitEnabled && !!token && !!appId,
    staleTime: 60_000,
    placeholderData: { manifest: null, config: DEFAULT_CONFIG },
  });
}

export function landUnitLabel(config?: MapperPluginConfig, plural = false): string {
  const lu = config?.layers?.land_units;
  if (plural) return lu?.label_plural || 'Land units';
  return lu?.label_singular || 'Land unit';
}
